<template>
  <div class="rdet">
    <div class="rdet__top">
      <roll-graphic :share="roll.restShare" :level="roll.status === 'active' ? roll.level : 'done'" :size="92" />
      <dl class="rdet__facts">
        <dt>Status</dt>
        <dd>{{ statusText }}</dd>
        <dt>Abschnitte</dt>
        <dd>{{ range }} · {{ roll.covered }} belegt</dd>
        <template v-if="roll.status === 'active'">
          <dt>Rest</dt>
          <dd>{{ roll.rest }} Abschnitte (≈ {{ meters }} m){{ ticketText }}</dd>
        </template>
        <dt>Kapazität</dt>
        <dd>{{ capacityText }}</dd>
        <dt>Verkäufe</dt>
        <dd>{{ roll.saleCount }}</dd>
        <template v-if="roll.printCount">
          <dt>Drucke ohne Verkauf</dt>
          <dd>{{ roll.printCount }}</dd>
        </template>
        <template v-if="roll.inserted">
          <dt>Eingelegt</dt>
          <dd>{{ insertedFact }}</dd>
        </template>
        <dt>Erster Druck</dt>
        <dd>{{ exact(roll.firstTime) }}</dd>
        <dt>Letzter Druck</dt>
        <dd>{{ exact(roll.lastTime) }}</dd>
        <dt>Drucker</dt>
        <dd>{{ printersText }}</dd>
        <template v-if="roll.serials.length">
          <dt>Seriennummer</dt>
          <dd>{{ roll.serials.join(', ') }}</dd>
        </template>
      </dl>
    </div>

    <section class="rdet__block" aria-label="Abschnittsband">
      <h3 class="rdet__h">Ganze Rolle</h3>
      <roll-section-band
        :ranges="roll.ranges"
        :print-ranges="roll.printRanges"
        :gaps="roll.gaps"
        :to="roll.capacity"
        :marker="roll.status === 'active' ? roll.max : null"
        scale
        tall
      />
      <template v-if="zoom">
        <h3 class="rdet__h">Belegter Bereich</h3>
        <roll-section-band
          :ranges="roll.ranges"
          :print-ranges="roll.printRanges"
          :gaps="roll.gaps"
          :from="roll.min"
          :to="roll.max"
          scale
          tall
        />
      </template>
      <p class="rdet__legend">
        <span class="rdet__item"><span class="rdet__key rdet__key--used"></span>belegt</span>
        <span v-if="roll.printCount" class="rdet__item"><span class="rdet__key rdet__key--print"></span>Druck ohne Verkauf</span>
        <span class="rdet__item"><span class="rdet__key rdet__key--bad"></span>Lücke unerklärt</span>
        <span class="rdet__item"><span class="rdet__key rdet__key--ok"></span>Lücke erklärt</span>
        <span class="rdet__item"><span class="rdet__key rdet__key--minor"></span>einzelner Abschnitt (unkritisch)</span>
      </p>
    </section>

    <section v-if="roll.warnings.length" class="rdet__block" aria-label="Hinweise">
      <h3 class="rdet__h">Hinweise</h3>
      <p v-for="(w, i) in roll.warnings" :key="i" class="rdet__warn" :class="'rdet__warn--' + w.level">
        <app-icon :name="w.level === 'info' ? 'info' : 'alertTriangle'" :size="14" />{{ w.text }}
      </p>
    </section>

    <section class="rdet__block" aria-label="Verlauf">
      <h3 class="rdet__h">Verlauf · neueste Abschnitte zuerst</h3>
      <ol class="rdet__list">
        <li v-for="item in history" :key="item.key" :class="item.gap ? 'rdet__gap rdet__gap--' + gapKind(item.gap) : 'rdet__ev'">
          <template v-if="item.gap">
            <strong>Lücke {{ rangeOf(item.gap.from, item.gap.to) }}</strong>
            <span>{{ item.gap.count }} {{ item.gap.count === 1 ? 'Abschnitt' : 'Abschnitte' }} · {{ gapRating(item.gap) }}: {{ item.gap.reason }}</span>
          </template>
          <template v-else>
            <span class="rdet__sec">{{ rangeOf(item.ev.min, item.ev.max) }}</span>
            <span class="rdet__evtext">
              <span>{{ exact(item.ev.time) }} · Drucker {{ item.ev.printer }}<template v-if="userOf(item.ev)"> · {{ userOf(item.ev) }}</template></span>
              <span v-if="item.ev.sale" class="rdet__ticket">{{ item.ev.sale.ticket_name || 'Ticket' }}<app-tag v-if="item.ev.sale.is_cancellation" class="rdet__tag" /></span>
              <span v-else class="rdet__ticket">{{ printLabel(item.ev.print) }}<app-tag kind="off" class="rdet__tag">ohne Verkauf</app-tag></span>
            </span>
          </template>
        </li>
      </ol>
    </section>
  </div>
</template>

<script>
// Verlauf einer Rolle im Detailbereich (Rollen, Kundenwunsch 27.10.2026): Rolle mit Rest und Kennzahlen, Abschnittsband
// der ganzen Rolle und – wenn der belegte Bereich kleiner ist – vergrößert nur dieser Bereich, Hinweise und alle Drucke
// nach Abschnitt absteigend, dazwischen die Lücken mit Erklärung (rot unerklärt, gelb erklärt, grau einzelner Abschnitt
// = unkritisch). Drucke ohne Verkauf (Testdruck, Abrechnung …, 05.11.2026) als eigene Zeilen mit Art und Beschreibung,
// im Band heller; die Einlage der Rolle (Papiereinzug bzw. Drucker verbunden) bei den Kennzahlen.
// Props: roll (aus analyzeRolls), capacity ({ value, learned, basis }), printerNames, now.
import RollGraphic from './RollGraphic';
import RollSectionBand from './RollSectionBand';
import { SECTION_MM, ticketsLeft, rangeText, printText, insertedText } from './rollTracking';
import { exactTime } from '@/utils/relativeTime';

export default {
  name: 'RollDetail',
  components: { RollGraphic, RollSectionBand },
  props: {
    roll: { type: Object, required: true },
    capacity: { type: Object, required: true },
    printerNames: { type: Object, default: () => ({}) }
  },
  computed: {
    statusText() {
      if (this.roll.status === 'active') {
        return 'im Einsatz in Drucker ' + this.roll.activeOn.join(', ');
      }
      return this.roll.full ? 'vollständig verbraucht' : 'beendet bei Abschnitt ' + this.roll.max;
    },
    range() {
      return rangeText(this.roll.min, this.roll.max);
    },
    meters() {
      return ((this.roll.rest * SECTION_MM) / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 });
    },
    ticketText() {
      const n = ticketsLeft(this.roll);
      return n === null ? '' : ', reicht für ~' + n + ' Verkäufe';
    },
    capacityText() {
      const c = this.capacity;
      return c.value + ' Abschnitte (' + (c.learned ? 'gelernt aus ' + c.basis + (c.basis === 1 ? ' verbrauchten Rolle' : ' verbrauchten Rollen') : 'geschätzt') + ')';
    },
    insertedFact() {
      const ins = this.roll.inserted;
      if (!ins) {
        return '';
      }
      const name = this.printerNames[ins.printer];
      return insertedText(ins) + ', ' + exactTime(ins.time)
        + ', Drucker ' + ins.printer + (name ? ' (' + name + ')' : '');
    },
    printersText() {
      return this.roll.printers.map((p) => p + (this.printerNames[p] ? ' (' + this.printerNames[p] + ')' : '')).join(', ');
    },
    zoom() {
      return this.roll.max - this.roll.min + 1 < this.roll.capacity * 0.6;
    },
    // Drucke nach höchstem Abschnitt absteigend, Lücken an ihrer Stelle dazwischen
    history() {
      const items = this.roll.events.map((ev, i) => ({ key: 'e' + i, ev, at: ev.max, order: 1 }));
      this.roll.gaps.forEach((gap, i) => items.push({ key: 'g' + i, gap, at: gap.to, order: 0 }));
      return items.sort((a, b) => b.at - a.at || a.order - b.order
        || (a.ev && b.ev ? String(b.ev.time).localeCompare(String(a.ev.time)) : 0));
    }
  },
  methods: {
    exact(value) {
      return exactTime(value);
    },
    rangeOf(a, b) {
      return rangeText(a, b);
    },
    userOf(ev) {
      return (ev.sale || ev.print).username;
    },
    printLabel(print) {
      return printText(print);
    },
    gapKind(gap) {
      return gap.explained ? 'ok' : gap.minor ? 'minor' : 'bad';
    },
    gapRating(gap) {
      return gap.explained ? 'erklärt' : gap.minor ? 'unkritisch' : 'unerklärt';
    }
  }
};
</script>

<style scoped>
.rdet {
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-size: 13px;
  line-height: 18px;
  color: var(--ink);
}
.rdet__top {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.rdet__facts {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;
  margin: 0;
  min-width: 0;
  font-variant-numeric: tabular-nums;
}
.rdet__facts dt {
  color: var(--ink-3);
}
.rdet__facts dd {
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
}
.rdet__block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.rdet__h {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: var(--ink-3);
}
/* Legende: Kästchen und Text bleiben beim Umbruch zusammen */
.rdet__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  margin: 0;
  font-size: 12px;
  color: var(--ink-3);
}
.rdet__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.rdet__key {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
}
.rdet__key--used {
  background: var(--d-std);
}
.rdet__key--print {
  background: var(--d-prv);
}
.rdet__key--bad {
  background: var(--st-late);
}
.rdet__key--ok {
  background: var(--st-warn);
}
.rdet__key--minor {
  background: var(--ink-4);
}
.rdet__warn {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  color: var(--ink-2);
}
.rdet__warn .app-icon {
  flex: none;
  margin-top: 2px;
}
.rdet__warn--error {
  color: var(--red);
}
.rdet__warn--warn .app-icon {
  color: var(--amber);
}
.rdet__list {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
}
.rdet__list > li {
  padding: 8px 10px;
  border-bottom: 1px solid var(--line-2);
}
.rdet__list > li:last-child {
  border-bottom: 0;
}
.rdet__ev {
  display: flex;
  gap: 12px;
}
.rdet__sec {
  flex: none;
  width: 76px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.rdet__evtext {
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: var(--ink-2);
  font-variant-numeric: tabular-nums;
}
.rdet__ticket {
  color: var(--ink-3);
}
.rdet__tag {
  margin-left: 6px;
}
.rdet__gap {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.rdet__gap--bad {
  background: var(--red-t);
  color: var(--red);
}
.rdet__gap--ok {
  background: var(--amber-t);
  color: var(--amber);
}
.rdet__gap--minor {
  background: var(--band);
  color: var(--ink-3);
}
</style>
